
import React, { useState } from 'react'
import { Formik } from 'formik';
import { CreateBrand } from '../../services/company_panel/brandService';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from "sweetalert2"
import Breadcrums from '../../components/company_panel/common/breadcrumbs';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { companyDetail, getCompany } from '../../helpers/commonFunctions';
import defaultImage from '../../assets/company/img/default_img.png'
import * as Yup from 'yup';
import { globalLoader } from '../../helpers/global';
import { FILE_SLUGS, UPLOAD_DIRS } from '../../utils/Constants';
import { GetGCPSignedUploadUrl } from '../../services/commonService';

const BrandAdd = () => {

  // page the user was on in the list (carried in the route) so we return there
  const params = useParams()
  const listPage = params?.pgno || 1;
  const breadcrumbs = [
    {
      title: "Brands",
      url: `/company/business/brand/list/${listPage}`
    },
    {
      title: "Create Brand",
      url: ""
    },
  ]
  const navigate = useNavigate()
  const [previewImage, setPreviewImage] = useState("");
  const [previewVideo, setPreviewVideo] = useState("");
  const [submitted, setSubmitted] = useState(false)

  const validationSchema = Yup.object().shape({
    logo: Yup.mixed()
      .required('Image is required')
      .test(
        'fileFormat',
        'Invalid Image Format',
        value => value && /\.(jpg|jpeg|png|webp|avif|gif)$/.test(value.name)
      )
      .test(
        'fileSize',
        "File size cannot be more than 5MB.",
        value => value && value.size <= 5 * 1024 * 1024
      ),
    name: Yup.string()
      .trim()
      .required('Brand name is required')
      .max(40, 'Brand name should not exceed 40 characters'),
    name_ar: Yup.string()
      .trim()
      .required('Brand name (Arabic) is required')
      .max(40, 'Brand name (Arabic) should not exceed 40 characters'),
    short_description: Yup.string()
      .trim()
      .required('Short description is required')
      .max(200, 'Short description cannot exceed 200 characters'),

    long_description: Yup.string()
      .trim()
      .required('Long description is required')
      .max(500, 'Long description cannot exceed 500 characters'),

    short_description_ar: Yup.string()
      .trim()
      .required('Short description (Arabic) is required')
      .max(200, 'Short description (Arabic) cannot exceed 200 characters'),

    long_description_ar: Yup.string()
      .trim()
      .required('Long description (Arabic) is required')
      .max(500, 'Long description (Arabic) cannot exceed 500 characters'),
  });

  return (
    <>
      <div className="page-header d-flex align-items-center">
        <div className="page-header-left">
          <Breadcrums data={breadcrumbs} />
          <h4>Brands</h4>
        </div>
        <div className="page-header-right ms-auto">
        </div>
      </div>
      <Formik
        initialValues={{
          logo: '',
          video : '',
          name: '',
          short_description: '',
          long_description: '',
          name_ar: '',
          short_description_ar: '',
          long_description_ar: '',
          is_payment_gateway_refund_bypass: false,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitted(true);
          globalLoader(true);
          try {
            const company_id = getCompany().company_id;
            const company_register_id = companyDetail().company_id;

            let logoFilename = '';
            let videoFilename = '';
            try {
              // Upload logo to GCP
              const logoSignedRes = await GetGCPSignedUploadUrl({
                original_name: values.logo.name,
                file_prefix: FILE_SLUGS.BRAND_LOGO,
                mime_type: values.logo.type,
                folder: `company-id-${company_register_id}/${UPLOAD_DIRS.BRAND_LOGO}`,
              });
              const { signedUrl: logoSignedUrl, filename: logoFile } = logoSignedRes?.data?.response;
              await fetch(logoSignedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': values.logo.type },
                body: values.logo,
              });
              logoFilename = logoFile;

              // Upload video to GCP only if selected
              if (values.video instanceof File) {
                const videoSignedRes = await GetGCPSignedUploadUrl({
                  original_name: values.video.name,
                  file_prefix: FILE_SLUGS.BRAND_VIDEO,
                  mime_type: values.video.type,
                  folder: `company-id-${company_register_id}/${UPLOAD_DIRS.BRAND_VIDEO}`,
                });
                const { signedUrl: videoSignedUrl, filename } = videoSignedRes?.data?.response;
                await fetch(videoSignedUrl, {
                  method: 'PUT',
                  headers: { 'Content-Type': values.video.type },
                  body: values.video,
                });
                videoFilename = filename;
              }
            } catch (uploadError) {
              console.log("Media upload error ====> ", uploadError);
              Swal.fire({
                customClass: 'swal-wide',
                icon: 'error',
                title: 'Something went wrong. Please try again.',
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                position: 'top-right',
              });
            }

            const formData = new FormData();
            formData.append('name', values?.name?.trim());
            formData.append('short_description', values.short_description);
            formData.append('long_description', values.long_description);
            formData.append('name_ar', values?.name_ar?.trim());
            formData.append('short_description_ar', values.short_description_ar);
            formData.append('long_description_ar', values.long_description_ar);
            formData.append('company_id', company_id);
            formData.append('is_payment_gateway_refund_bypass', values.is_payment_gateway_refund_bypass ? 'true' : 'false');
            if (logoFilename) formData.append('logo', logoFilename);
            if (videoFilename) formData.append('video', videoFilename);

            const response = await CreateBrand(formData);

            globalLoader(false);
            setSubmitting(false);
            setSubmitted(false);

            if (response.data.status === 1 || response.data.status === "1") {
              Swal.fire({
                customClass: 'swal-wide',
                icon: 'success',
                title: response.data.message,
                showConfirmButton: false,
                timer: 1500,
                toast: true,
                position: 'top-right',
              });
              const evt = new CustomEvent('brand-list-modified', { detail: {} });
              document.dispatchEvent(evt);
              setTimeout(() => {
                navigate(`/company/business/brand/list/${listPage}`);
              }, 500);
            } else {
              Swal.fire({
                customClass: 'swal-wide',
                icon: 'error',
                title: response.data.message,
                showConfirmButton: false,
                timer: 5000,
                toast: true,
                position: 'top-right',
              });
            }
          } catch (error) {
            globalLoader(false);
            setSubmitting(false);
            setSubmitted(false);
            console.log("error ====> ", error);
            Swal.fire({
              customClass: 'swal-wide',
              icon: 'error',
              title: 'Something went wrong. Please try again.',
              showConfirmButton: false,
              timer: 3000,
              toast: true,
              position: 'top-right',
            });
          }
        }}
      >{({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="card custom-card">
            <div className="card-body">
              <div className="row row-sm">
                <div className='col-md-12'>
                  <div className='upload-logo d-flex align-items-center gap-3 mb-3'>

                    {previewImage ?
                      <div className="avatar avatar-xl">
                        <img alt="avatar" src={previewImage} className={previewImage ? "" : "d-none"} />
                      </div>
                      :
                      <div className="avatar avatar-xl">
                        <img alt="avatar" src={defaultImage} />
                      </div>
                    }
                    <div className='btn btn-black btn-file-type'>Upload logo <input id="logo" name="logo" accept="image/*" type="file" onChange={(event) => {
                      setFieldValue("logo", event.currentTarget.files[0] || "");
                      { event.currentTarget.files.length == 1 ? (setPreviewImage(URL.createObjectURL(event.currentTarget.files[0]))) : (setPreviewImage("")) }
                    }}
                    /></div>
                  </div>
                  <span className='text-danger d-flex text-left' id='errortext'>
                    {errors.logo && touched.logo && errors.logo}
                  </span>
                </div>
                <div className='col-lg-6 text-center form-group mb-3'>
                  <label htmlFor='name' className='text-left d-flex'>Brand Name<span className="requirestar">*</span></label>
                  <input name='name' id="name" type='text' placeholder='Enter brand name' onChange={handleChange} onBlur={handleBlur} value={values.name} className='form-control' />
                  <span className='text-danger d-flex text-left'>{errors.name && touched.name && errors.name}</span>
                </div>
                <div className='col-lg-6 text-center form-group mb-3'>
                  <label htmlFor='name_ar' className='text-left d-flex'>Brand Name (Arabic)<span className="requirestar">*</span></label>
                  <input name='name_ar' id="name_ar" placeholder='Enter brand name (Arabic)' type='text' onChange={handleChange} onBlur={handleBlur} value={values.name_ar} className='form-control' />
                  <span className='text-danger d-flex text-left'>{errors.name_ar && touched.name_ar && errors.name_ar}</span>
                </div>
                <div className='col-lg-6 text-center form-group required mb-3'>
                  <label htmlFor='short_description' className='text-left d-flex'>Short description<span className="requirestar">*</span> </label>
                  <textarea name='short_description' placeholder='Enter short description' id="short_description" rows="2" type='text' onChange={handleChange} onBlur={handleBlur} value={values.short_description} className='form-control' style={{ height: "100px" }} />
                  <span className='text-danger d-flex text-left'>{errors.short_description && touched.short_description && errors.short_description}</span>
                </div>
                <div className='col-lg-6 text-center form-group required mb-3'>
                  <label htmlFor='short_description_ar' className='text-left d-flex'>Short description (Arabic)<span className="requirestar">*</span> </label>
                  <textarea name='short_description_ar' placeholder='Enter short description (Arabic)' id="short_description_ar" rows="2" type='text' onChange={handleChange} onBlur={handleBlur} value={values.short_description_ar} className='form-control' style={{ height: "100px" }} />
                  <span className='text-danger d-flex text-left'>{errors.short_description_ar && touched.short_description_ar && errors.short_description_ar}</span>
                </div>
                <div className='col-lg-6 text-center form-group required mb-3'>
                  <label htmlFor='long_description' className='text-left d-flex'>Long description<span className="requirestar">*</span> </label>
                  <textarea name='long_description' placeholder='Enter long description' id="long_description" rows="2" type='text' onChange={handleChange} onBlur={handleBlur} value={values.long_description} className='form-control' style={{ height: "100px" }} />
                  <span className='text-danger d-flex text-left'>{errors.long_description && touched.long_description && errors.long_description}</span>
                </div>
                <div className='col-lg-6 text-center form-group required mb-3'>
                  <label htmlFor='long_description_ar' className='text-left d-flex'>Long description (Arabic)<span className="requirestar">*</span> </label>
                  <textarea name='long_description_ar' placeholder='Enter long description (Arabic)' id="long_description_ar" rows="2" type='text' onChange={handleChange} onBlur={handleBlur} value={values.long_description_ar} className='form-control' style={{ height: "100px" }} />
                  <span className='text-danger d-flex text-left'>{errors.long_description_ar && touched.long_description_ar && errors.long_description_ar}</span>
                </div>
                <div className='col-md-12'>
                  <label className='text-left d-flex mb-2' style={{ fontWeight: 600 }}>Brand Video</label>
                  <div className='upload-logo d-flex align-items-center gap-3 mb-3 flex-wrap'>
                    <div className="brand-video-preview">
                    {previewVideo ? (
                        <video muted autoPlay loop controls playsInline>
                          <source src={previewVideo} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                    ) : (
                        <div className="brand-video-preview__placeholder">
                          <i className="ti ti-video"></i>
                          <span>No video uploaded</span>
                        </div>
                    )}
                    </div>
                    <div className='btn btn-black btn-file-type'>Upload Brand Video <input id="video" name="video" accept="video/*" type="file" onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        if (file && file.size > 40 * 1024 * 1024) {
                          alert("File is too large! Max size is 40MB.");
                        } else {
                          setFieldValue("video", file || "");
                          setPreviewVideo(file ? URL.createObjectURL(file) : "");
                        }
                    }}
                    /></div>
                  </div>
                </div>
                <div className='col-lg-6 form-group mb-3'>
                  <label className='text-left d-flex'>Payment Gateway Refund Bypass</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input cp"
                      type="checkbox"
                      id="is_payment_gateway_refund_bypass"
                      name="is_payment_gateway_refund_bypass"
                      checked={!!values.is_payment_gateway_refund_bypass}
                      onChange={(event) => setFieldValue("is_payment_gateway_refund_bypass", event.target.checked)}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <button className="btn btn-primary mr-2" type="submit" disabled={submitted ? true : null}>
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
      </Formik>
      <ToastContainer
        position="top-right"
        hideProgressBar={false}
        autoClose={true | 1000}
        newestOnTop={true}
        closeOnClick={false}
        draggable={false}
        rtl={false}
      />
    </>
  );
}

export default BrandAdd